// Plan 14-03 — PieceDragLayer (MUI-02 piece-drag gesture).
//
// Pointer-events HTML overlay sitting above the PixiJS canvas. Captures
// touch/mouse/pen drag for a SINGLE (from, to) cell pair — the Phase B
// `{kind:'move', from, to}` selection. Mirrors WordTraceLayer's pointer-
// capture idiom (setPointerCapture + document.elementFromPoint hit-test).
//
// Consumer contract (MatchScreen):
//   - The PixiJS <MatchCanvas> wrapper MUST set `pointerEvents: 'none'` so
//     DOM pointer events fall through to this overlay (Pitfall 4 in
//     14-RESEARCH.md). The layer mounts as an absolute-positioned sibling
//     directly above the canvas in z-order, and IS clipped to the same
//     360x360 board area.
//   - On a successful drag the layer calls `onComplete(from, to)`. The
//     consumer should setPhaseB({kind:'move', from, to}).
//
// Drag eligibility (DEC-021 client guard — server is authority):
//   - `playerView.board[from].piece_owner_slot === playerView.your_slot`
//     gates pointerdown: only the caller's own pieces are draggable. A
//     pointerdown on opponent's piece OR an empty cell is a no-op.
//
// Invalid drops (silent abort, no callback):
//   - target cell === source cell (no movement)
//   - pointerup with no resolved source cell (drag never started)
//   - pointercancel (OS-cancelled gesture)
//
// DEC-021 server authority: the move legality (piece kind / role / path /
// capture-own-piece etc.) is decided by the backend's `validateMove`
// (apps/backend/src/match/validation/chess.ts). This layer is ergonomic
// only — a tampered (from, to) reaches the server's 400 VALIDATION_FAILED
// path regardless of what this overlay decided.

import { type JSX, useCallback, useRef, useState } from 'react'
import { BOARD_COLS } from '../../game/layers/board-layout'
import type { PlayerView } from '../../game/PlayerViewSchema'
import { resolveDragSource } from '../../game/staged-move'

/** 8-way adjacency on the row-major (6-col) field — used to scope the #7
 *  blank-capture cue to a genuinely adjacent enemy. */
function areCellsAdjacent(a: number, b: number): boolean {
  const dr = Math.abs(Math.floor(a / BOARD_COLS) - Math.floor(b / BOARD_COLS))
  const dc = Math.abs((a % BOARD_COLS) - (b % BOARD_COLS))
  return dr <= 1 && dc <= 1 && dr + dc > 0
}

export interface PieceDragLayerProps {
  readonly playerView: PlayerView
  readonly onComplete: (from: number, to: number) => void
  /** Cell size in CSS pixels. Defaults to 60 (the 360pt board / 6 cells). */
  readonly cellSize?: number
  /**
   * When false the layer keeps `pointer-events: none`, ignores all pointer
   * input, and yields the z-stack to WordTraceLayer / MatchGrid. Default is
   * true so the existing isolation tests keep working.
   *
   * MatchScreen drives this from `boardView === 'chess'` — DEC-023/DEC-037
   * make the Chess and Letters views mutually exclusive gesture surfaces, so
   * piece-drag is live only in the Chess view (otherwise the always-on
   * WordTraceLayer, seeded to Phase A 'word' for the whole turn, would still
   * sit on top and swallow the drag).
   */
  readonly enabled?: boolean
  /**
   * Server-authored legal destinations per own-piece cell
   * (`PlayerView.your_legal_moves`). Keyed by source cell_idx; gates which
   * drops succeed and drives the on-pickup target highlight.
   */
  readonly legalMoves: Readonly<Record<number, ReadonlyArray<number>>>
  /**
   * The currently staged (optimistic) move, or null. When present the source
   * piece has been visually relocated to `to`, so a pointerdown on `to`
   * re-picks the original `from`, and a drop back on `from` cancels.
   */
  readonly stagedMove: { from: number; to: number } | null
  /** Called when the staged piece is dropped back on its origin → un-stage. */
  readonly onCancelMove: () => void
  /**
   * #7 — invoked when the player drags an un-empowered "blank" piece onto an
   * ADJACENT enemy and the capture is rejected. Blanks can only step one
   * orthogonal cell to an EMPTY square; capturing requires empowerment
   * (apps/backend/src/match/validation/chess.ts), so the server omits the
   * capture from your_legal_moves. Lets the consumer explain the rejection
   * instead of a silent snap-back. NOT fired for empty-cell or non-adjacent
   * illegal drops, nor for empowered pieces. Optional.
   */
  readonly onBlankCaptureBlocked?: (from: number, to: number) => void
  /**
   * ARCA-591 — invoked on a discrete same-cell tap (pointerdown + pointerup on
   * the SAME own-piece cell, with no move to another cell). This gesture is a
   * no-op for moves (a move needs a destination), so it is repurposed for the
   * re-empower inspector: the consumer (MatchScreen) opens the ExtendModal only
   * when the tapped cell is an own ALREADY-empowered piece, and ignores it
   * otherwise. NOT fired for a real move (drop on a different cell), an
   * opponent-piece press (never an accepted source), or a staged-move
   * cancel/re-pick. Optional.
   */
  readonly onPieceTap?: (cellIdx: number) => void
}

interface CellHit {
  idx: number
  el: HTMLElement
}

export function PieceDragLayer({
  playerView,
  onComplete,
  cellSize = 60,
  enabled = true,
  legalMoves,
  stagedMove,
  onCancelMove,
  onBlankCaptureBlocked,
  onPieceTap,
}: PieceDragLayerProps): JSX.Element {
  const [source, setSource] = useState<number | null>(null)
  const [ghostPos, setGhostPos] = useState<{ x: number; y: number } | null>(
    null,
  )
  // DOM-positioned highlight rects for the picked-up piece's legal targets.
  const [highlights, setHighlights] = useState<
    ReadonlyArray<{
      idx: number
      capture: boolean
      left: number
      top: number
      width: number
      height: number
    }>
  >([])
  // Mutable shadow so the up/move handlers read the latest source without
  // waiting on a React re-render (same pattern as WordTraceLayer's pathRef).
  const sourceRef = useRef<number | null>(null)
  const layerRef = useRef<HTMLDivElement>(null)

  /**
   * Hit-test: resolve a viewport (x, y) pair into a `[data-cell-idx]` cell
   * index, or null if the pointer is not over any cell.
   *
   * When enabled this layer is the TOP-most pointer surface (zIndex above
   * WordTraceLayer + MatchGrid), so `elementFromPoint` would otherwise
   * resolve to the layer itself. We momentarily disable our own pointer-
   * events for the hit-test so it lands on the MatchGrid cell button beneath
   * — the same trick WordTraceLayer.cellAtPoint uses.
   */
  // Upper bound is the LIVE board length (Runespring grows 36→48→60), not a
  // fixed 36 — a hardcoded `idx > 35` silently dropped every drag that landed
  // on a grown-board cell (cell_idx ≥ 36), making the flop/river rows
  // undraggable. Track the live length via a ref so the memoized hit-test stays
  // current without re-subscribing on every board update.
  const boardLenRef = useRef(playerView.board.length)
  boardLenRef.current = playerView.board.length
  const cellAtPoint = useCallback((x: number, y: number): CellHit | null => {
    const layer = layerRef.current
    const prev = layer?.style.pointerEvents
    if (layer) layer.style.pointerEvents = 'none'
    try {
      const el = document.elementFromPoint(x, y) as HTMLElement | null
      if (el === null) return null
      const cell = el.closest<HTMLElement>('[data-cell-idx]')
      if (cell === null) return null
      const raw = cell.dataset.cellIdx
      if (raw === undefined) return null
      const idx = Number(raw)
      if (!Number.isInteger(idx) || idx < 0 || idx >= boardLenRef.current) {
        return null
      }
      return { idx, el: cell }
    } finally {
      if (layer) layer.style.pointerEvents = prev ?? ''
    }
  }, [])

  /**
   * Is the piece at `cellIdx` ownable by the caller (DEC-021 client guard)?
   * A cell is drag-eligible iff it holds the caller's OWN piece:
   * `piece_owner_slot === your_slot` AND a non-null `piece_kind`. A blank
   * cell (no piece) has both fields null and fails the check. Defensive
   * null-check for `board[cellIdx]` (e.g. a malformed view). MUST mirror the
   * backend `validateMove` (chess.ts), which 400-rejects any move whose
   * `fromCell.piece_owner_slot !== slot`.
   */
  const isOwnPiece = useCallback(
    (cellIdx: number): boolean => {
      const cell = playerView.board[cellIdx]
      if (cell === undefined) return false
      return (
        cell.piece_owner_slot === playerView.your_slot &&
        cell.piece_kind !== null
      )
    },
    [playerView],
  )

  const reset = useCallback(() => {
    sourceRef.current = null
    setSource(null)
    setGhostPos(null)
    setHighlights([])
  }, [])

  /**
   * Position a DOM highlight square over each legal-target cell. Each square is
   * placed over its matching `[data-cell-idx]` button via getBoundingClientRect
   * minus the layer rect (the layer is offset from the viewport origin). A
   * capture target — a cell the REAL board shows owned by the opponent — is
   * flagged so it can render a distinct ring. happy-dom returns zero-size
   * rects, so tests assert on count/presence rather than geometry.
   */
  const computeHighlights = useCallback(
    (src: number) => {
      const targets = legalMoves[src] ?? []
      const layerRect = layerRef.current?.getBoundingClientRect()
      const oppSlot = playerView.your_slot === 0 ? 1 : 0
      const next = targets.map((idx) => {
        const btn = document.querySelector<HTMLElement>(
          `[data-cell-idx="${idx}"]`,
        )
        const r = btn?.getBoundingClientRect()
        const cell = playerView.board[idx]
        const capture = cell !== undefined && cell.piece_owner_slot === oppSlot
        return {
          idx,
          capture,
          left: (r?.left ?? 0) - (layerRect?.left ?? 0),
          top: (r?.top ?? 0) - (layerRect?.top ?? 0),
          width: r?.width ?? 0,
          height: r?.height ?? 0,
        }
      })
      setHighlights(next)
    },
    [legalMoves, playerView],
  )

  /**
   * Convert a viewport (client) coordinate into a coordinate local to this
   * layer's box. The board container is centered (maxWidth + margin auto +
   * padding), so the layer's top-left is NOT at the viewport origin — the
   * ghost MUST be positioned relative to the layer or it lands hundreds of
   * px away from the pointer (often off-screen).
   */
  const toLocal = useCallback(
    (clientX: number, clientY: number): { x: number; y: number } => {
      const rect = layerRef.current?.getBoundingClientRect()
      return { x: clientX - (rect?.left ?? 0), y: clientY - (rect?.top ?? 0) }
    },
    [],
  )

  const onPointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (!enabled) return
      try {
        e.currentTarget.setPointerCapture(e.pointerId)
      } catch {
        // Some test environments + older browsers throw; the gesture still
        // works via bubbling pointermove on the element itself.
      }
      const hit = cellAtPoint(e.clientX, e.clientY)
      if (hit === null) return
      // Resolve the true source: when a staged move exists and the pressed
      // cell is its target, the previewed piece is a proxy for the original
      // source — re-pick that source.
      const src = resolveDragSource(hit.idx, stagedMove)
      // DEC-021 guard — accept only the caller's own pieces, OR a press on the
      // staged-target cell (whose underlying source is owned by the caller).
      const acceptable =
        isOwnPiece(src) || (stagedMove !== null && hit.idx === stagedMove.to)
      if (!acceptable) return
      sourceRef.current = src
      setSource(src)
      setGhostPos(toLocal(e.clientX, e.clientY))
      computeHighlights(src)
    },
    [cellAtPoint, isOwnPiece, enabled, toLocal, stagedMove, computeHighlights],
  )

  const onPointerMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (sourceRef.current === null) return
      setGhostPos(toLocal(e.clientX, e.clientY))
    },
    [toLocal],
  )

  const onPointerUp = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      const src = sourceRef.current
      if (src === null) {
        // Drag never started (e.g. pointerdown on opponent piece).
        reset()
        return
      }
      const hit = cellAtPoint(e.clientX, e.clientY)
      // Reset before invoking the callback so React can't see a partial
      // mid-drag state if onComplete triggers a synchronous re-render.
      reset()
      if (hit === null) return
      const drop = hit.idx
      // Drop the staged piece back on its origin → un-stage the move.
      if (stagedMove !== null && drop === stagedMove.from) {
        onCancelMove()
        return
      }
      if (drop === src) {
        // ARCA-591 — a same-cell tap (no move) is a no-op for movement; surface
        // it as a piece-inspect so the consumer can offer re-empower on an own
        // empowered piece. `src` is already an accepted own-piece source.
        onPieceTap?.(src)
        return
      }
      const legal = legalMoves[src] ?? []
      if (legal.includes(drop)) {
        onComplete(src, drop)
        return
      }
      // Illegal target — snap back with a brief invalid flash, no callback.
      hit.el.dataset.cellFlash = 'invalid'
      setTimeout(() => {
        if (hit.el.dataset.cellFlash === 'invalid') {
          delete hit.el.dataset.cellFlash
        }
      }, 200)
      // #7 — explain the most confusing rejection: a blank (un-empowered) piece
      // trying to capture an ADJACENT enemy. Surface the "why" instead of a
      // silent snap-back. Scoped to adjacent enemy-occupied cells so empty-cell
      // / out-of-range drops stay silent, and to blanks so empowered pieces
      // (whose illegal drops have other causes) never trip it.
      if (onBlankCaptureBlocked !== undefined) {
        const fromCell = playerView.board.find((c) => c.cell_idx === src)
        const toCell = playerView.board.find((c) => c.cell_idx === drop)
        const oppSlot = playerView.your_slot === 0 ? 1 : 0
        if (
          fromCell?.piece_kind === 'blank' &&
          fromCell.role_kind === null &&
          fromCell.piece_owner_slot === playerView.your_slot &&
          toCell?.piece_owner_slot === oppSlot &&
          areCellsAdjacent(src, drop)
        ) {
          onBlankCaptureBlocked(src, drop)
        }
      }
    },
    [
      cellAtPoint,
      onComplete,
      reset,
      stagedMove,
      legalMoves,
      onCancelMove,
      onBlankCaptureBlocked,
      onPieceTap,
      playerView,
    ],
  )

  const onPointerCancel = useCallback(() => {
    // OS cancelled the gesture — silent abort. No callback.
    reset()
  }, [reset])

  // Cell size is reserved for the (optional) ghost-piece visual sizing.
  // Currently we render a minimal dot — Plan 04 wires the full glyph from
  // the role table once piece-art is in place. The prop stays for forward
  // compat (consumer can pass a non-60 size for landscape layouts).
  void cellSize

  return (
    <div
      ref={layerRef}
      data-screen-region="piece-drag-layer"
      data-drag-active={source !== null ? 'true' : 'false'}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerCancel}
      style={{
        position: 'absolute',
        inset: 0,
        touchAction: 'none',
        // Live only in the Chess view (DEC-023/DEC-037). When enabled this
        // overlay receives DOM pointer events; the MatchCanvas sibling sets
        // pointerEvents:'none' so its pixi canvas does NOT swallow the
        // gesture (Pitfall 4 in 14-RESEARCH). When disabled (Letters view)
        // it goes transparent so WordTraceLayer / MatchGrid own the pointer.
        pointerEvents: enabled ? 'auto' : 'none',
        // Sit ABOVE MatchGrid (no zIndex) and WordTraceLayer (zIndex:3) when
        // live so the Chess-view drag is captured before either of them.
        zIndex: enabled ? 4 : undefined,
      }}
    >
      {source !== null
        ? highlights.map((h) => (
            <div
              key={h.idx}
              aria-hidden="true"
              data-legal-target="true"
              {...(h.capture ? { 'data-capture': 'true' } : {})}
              style={{
                position: 'absolute',
                left: h.left,
                top: h.top,
                width: h.width,
                height: h.height,
                boxSizing: 'border-box',
                borderRadius: h.capture ? '50%' : '8px',
                border: h.capture
                  ? '3px solid var(--color-cta-destructive)'
                  : '3px solid var(--color-cta-primary)',
                backgroundColor: h.capture
                  ? 'transparent'
                  : 'color-mix(in srgb, var(--color-cta-primary) 22%, transparent)',
                pointerEvents: 'none',
              }}
            />
          ))
        : null}
      {ghostPos !== null && source !== null ? (
        <div
          aria-hidden="true"
          data-test="drag-ghost"
          style={{
            position: 'absolute',
            // Center the ghost on the pointer; the layer is positioned by
            // its parent's offset, so client-coords need to be relative.
            // We use translate(-50%, -50%) to center; the actual layer
            // offset is the parent's offset. Inline `left`/`top` carry
            // the raw client coords — for production the layer's parent
            // is at (0,0) of the board container so client == local for
            // a single-board page. For a multi-board test rig this would
            // need a getBoundingClientRect subtraction; not needed today.
            left: ghostPos.x,
            top: ghostPos.y,
            transform: 'translate(-50%, -50%)',
            width: '32px',
            height: '32px',
            borderRadius: '50%',
            backgroundColor: 'var(--color-cta-primary)',
            opacity: 0.6,
            pointerEvents: 'none',
          }}
        />
      ) : null}
    </div>
  )
}
